import { GridColumn as Column } from '@progress/kendo-react-grid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  DeleteButton,
  SecondaryButton
} from 'src/components/shared/Button/Button';
import { BooleanCell } from 'src/components/shared/DataGrid/BooleanCell';
import { BooleanFilterCell } from 'src/components/shared/DataGrid/BooleanFilterCell';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { GridCountCell } from 'src/components/shared/DataGrid/GridCountCell';
import {
  HeaderSection,
  HeaderSectionLeft,
  HeaderSectionRight
} from 'src/components/shared/HeaderSection/HeaderSection';
import { Modal } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { Header1 } from 'src/components/shared/Typography/Header1';
import { useIsFormDirty } from 'src/hooks/useIsFormDirty';
import { useReduxDispatch } from 'src/store';
import {
  InstrumentGroupDetailDto,
  InstrumentGroupDto
} from 'src/store/instrumentgroups/instrumentgroup';
import { mapInstrumentGroupDetailToEdit } from 'src/store/instrumentgroups/instrumentgroup.map';
import {
  createInstrumentGroup,
  deleteInstrumentGroup,
  editInstrumentGroup,
  fetchInstrumentGroups
} from 'src/store/instrumentgroups/instrumentgroups.actions';
import { InstrumentGroupApi } from 'src/store/instrumentgroups/instrumentgroups.api';
import { selectInstrumentGroups } from 'src/store/instrumentgroups/instrumentgroups.selectors';
import { isLoadingInstrumentGroups } from 'src/store/requests/requests.selectors';
import { InstrumentGroupForm } from './InstrumentGroupForm';

export const InstrumentGroupsPage = () => {
  const [currentInstrumentGroup, setCurrentInstrumentGroup] =
    useState<InstrumentGroupDto | null>(null);
  const [instrumentGroup, setInstrumentGroup] =
    useState<Nullable<InstrumentGroupDetailDto>>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [isFormDirty, callbackRef] = useIsFormDirty();

  const { t } = useTranslation();
  const dispatch = useReduxDispatch();

  const instrumentGroups = useSelector(selectInstrumentGroups);
  const loadingInstrumentGroups = useSelector(isLoadingInstrumentGroups);

  useEffect(() => {
    dispatch(fetchInstrumentGroups());
  }, [dispatch]);

  useEffect(() => {
    if (currentInstrumentGroup) {
      setShowDeleteModal(true);
    }
  }, [currentInstrumentGroup]);

  const onCancel = () => {
    setCurrentInstrumentGroup(null);
    setInstrumentGroup(null);
    setShowDeleteModal(false);
    setShowEditModal(false);
  };

  const onCreate = () => {
    setShowEditModal(true);
  };

  const onEdit = async (instrumentGroup: InstrumentGroupDto) => {
    const response = await InstrumentGroupApi.getById(instrumentGroup.id);
    if (response.ok) {
      setInstrumentGroup(response.data);
      setShowEditModal(true);
    }
  };

  const onDelete = (instrumentGroup: InstrumentGroupDto) => {
    setCurrentInstrumentGroup(instrumentGroup);
  };

  const onConfirmDelete = () => {
    if (currentInstrumentGroup) {
      dispatch(
        deleteInstrumentGroup(currentInstrumentGroup.id, currentInstrumentGroup)
      );
      setCurrentInstrumentGroup(null);
      setShowDeleteModal(false);
    }
  };

  const onSubmit = async (instrumentGroup: InstrumentGroupDetailDto) => {
    const dto = mapInstrumentGroupDetailToEdit(instrumentGroup);
    if (dto.id) {
      await dispatch(editInstrumentGroup(dto.id, dto));
      onCancel();
    } else {
      await dispatch(createInstrumentGroup(dto));
      onCancel();
    }
  };

  return (
    <>
      <HeaderSection className="mb-32">
        <HeaderSectionLeft>
          <Header1>{t('instrumentGroup.plural')}</Header1>
          <BodyType>
            {`${t('crudtext')} ${t('instrumentGroup.plural')}`}
          </BodyType>
        </HeaderSectionLeft>
        <HeaderSectionRight>
          <SecondaryButton onClick={onCreate} icon="la-plus">
            {`${t('create.verb')} ${t('instrumentGroup.singular')}`}
          </SecondaryButton>
        </HeaderSectionRight>
      </HeaderSection>

      <DataGrid
        items={instrumentGroups}
        loading={loadingInstrumentGroups}
        onRowClick={({ dataItem }) => onEdit(dataItem)}
        initialSort={[{ field: 'name', dir: 'asc' }]}
      >
        <Column
          field="name"
          title={t('props.name')}
          footerCell={() => (
            <GridCountCell
              count={instrumentGroups?.length}
              label={t('instrumentGroup.plural')}
            />
          )}
        />
        <Column field="description" title={t('props.description')} />
        <Column
          width="160px"
          field="corrected"
          title={t('instrumentGroup.corrected')}
          cell={props => <BooleanCell {...props} />}
          filterCell={props => (
            <BooleanFilterCell
              trueLabel={t('props.true')}
              falseLabel={t('props.false')}
              {...props}
            />
          )}
        />
        <Column
          width="100px"
          filterable={false}
          cell={props => (
            <td>
              <DeleteButton onClick={() => onDelete(props.dataItem)} />
            </td>
          )}
        />
      </DataGrid>

      <Modal
        header={`${instrumentGroup ? t('edit') : t('create.verb')} ${t(
          'instrumentGroup.singular'
        )}`}
        confirmText={t('save.verb')}
        onClose={() => onCancel()}
        open={showEditModal}
        allowCommonExitStrategies={!isFormDirty}
      >
        <InstrumentGroupForm
          instrumentGroup={instrumentGroup}
          onCancel={onCancel}
          onSubmit={onSubmit}
          ref={callbackRef}
        />
      </Modal>

      <Modal
        width={500}
        header={`${t('delete.verb')} ${t('instrumentGroup.singular')}`}
        confirmText={t('delete.verb')}
        onClose={() => onCancel()}
        onConfirm={() => onConfirmDelete()}
        open={showDeleteModal}
        showActions
      >
        <BodyType>
          {t('instrumentGroup.delete')}
          <q>{currentInstrumentGroup?.name}</q>?
        </BodyType>
      </Modal>
    </>
  );
};
